'use client';

import {
  faUser,
  faUserAlien,
  faUserAstronaut,
  faUserChef,
  faUserCowboy,
  faUserCrown,
  faUserDoctorHairLong,
  faUserGraduate,
  faUserHairBuns,
  faUserHelmetSafety,
  faUserNinja,
  faUserPilot,
  faUserRobot,
  faUserSecret,
  faUserShakespeare,
  faUserTieHairLong,
  faUserVneckHairLong,
} from '@fortawesome/pro-solid-svg-icons';
import { ComponentPropsWithoutRef } from 'react';

import { AvatarSize, sizeToContainerSize, sizeToFontSize } from './Avatar';
import { Icon, IconDefinition } from '../Icon/Icon';

const iconSize: Record<AvatarSize, number> = {
  xxs: 16,
  xs: 16,
  sm: 20,
  md: 24,
  lg: 32,
  xl: 40,
  '2xl': 48,
  '3xl': 72,
};

export type AvatarIcon =
  | 'faUser'
  | 'faUserAlien'
  | 'faUserAstronaut'
  | 'faUserChef'
  | 'faUserSecret'
  | 'faUserDoctorHairLong'
  | 'faUserHelmetSafety'
  | 'faUserTieHairLong'
  | 'faUserGraduate'
  | 'faUserHairBuns'
  | 'faUserVneckHairLong'
  | 'faUserNinja'
  | 'faUserPilot'
  | 'faUserShakespeare'
  | 'faUserRobot'
  | 'faUserCrown'
  | 'faUserCowboy';

type IconStrToIconMap = Map<AvatarIcon, IconDefinition>;

const iconStrToIconMap: IconStrToIconMap = new Map([
  ['faUser', faUser],
  ['faUserAlien', faUserAlien],
  ['faUserAstronaut', faUserAstronaut],
  ['faUserChef', faUserChef],
  ['faUserSecret', faUserSecret],
  ['faUserDoctorHairLong', faUserDoctorHairLong],
  ['faUserHelmetSafety', faUserHelmetSafety],
  ['faUserTieHairLong', faUserTieHairLong],
  ['faUserGraduate', faUserGraduate],
  ['faUserHairBuns', faUserHairBuns],
  ['faUserVneckHairLong', faUserVneckHairLong],
  ['faUserNinja', faUserNinja],
  ['faUserPilot', faUserPilot],
  ['faUserShakespeare', faUserShakespeare],
  ['faUserRobot', faUserRobot],
  ['faUserCrown', faUserCrown],
  ['faUserCowboy', faUserCowboy],
]);

export interface AnonAvatarProps extends ComponentPropsWithoutRef<'div'> {
  icon: AvatarIcon;
  size?: AvatarSize;
}

const renderIcon = (icon: AvatarIcon, size: AvatarSize) => {
  if (!icon) return null;

  return (
    <Icon
      icon={iconStrToIconMap.get(icon) ?? faUserAlien}
      style={{
        width: iconSize[size],
        height: iconSize[size],
      }}
      className='text-slate-500 opacity-40 shadow-sm'
    />
  );
};

/**
 * AnonymousAvatar is used to represent users who are not logged in.
 * @see {@link https://stylus.design/components/avatar#anonymous-users}
 */
export const AnonymousAvatar: React.FC<AnonAvatarProps> = ({ icon, size = 'md' }) => {
  const containerSize = sizeToContainerSize[size];

  return (
    <div
      style={{
        width: containerSize,
        height: containerSize,
        fontSize: sizeToFontSize[size],
      }}
      className='flex items-end justify-center overflow-y-hidden rounded-full bg-slate-100 [text-shadow:0_1px_1px_rgba(49,46,129,0.25)]'
    >
      {renderIcon(icon, size)}
    </div>
  );
};
